import { useState } from 'react';
import { Formik, Form, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import AddSingleStudent from './AddSingleStudent';
import AddMultipleStudents from './AddMultipleStudents';
import ModeSelector from '../../components/ModeSelector';
import Button from '../../components/Button';
import { Icon } from '@iconify/react';
import usersSolid from '@iconify/icons-clarity/users-solid';
import avatarSolid from '@iconify/icons-clarity/avatar-solid';
import { checkStudentEmailVerdict } from '../../BackendInterface';
import { EMAIL_CHECK_FAILED_ERROR, getBatchEmailError, getStudentEmailVerdictError } from '../studentEmailVerdict';
import styles from './AddStudents.module.scss';

interface Props {
  hideModal: () => void;
  defaultStudentPassword: string;
  studentNumberLimit: number;
  totalNumberOfStudents: number;
  onAddStudents: (arg0: Array<string>, arg1: Array<string>, arg2: Array<string>) => void;
}

interface FormValues {
  [key: string]: string;
}

type EmailCheckTarget = {
  fieldName: string;
  email: string;
};

const AddStudents = ({
  hideModal,
  defaultStudentPassword,
  studentNumberLimit,
  totalNumberOfStudents,
  onAddStudents,
}: Props): JSX.Element => {
  const [studentNames, setStudentNames] = useState<string[]>([]);
  const [studentPasswords, setStudentPasswords] = useState<string[]>([]);
  const [studentEmails, setStudentEmails] = useState<string[]>([]);
  const [isAddMultiple, setIsAddMultiple] = useState(false);
  const [validationAttemptCount, setValidationAttemptCount] = useState(0);
  const addSingleInitialValues: FormValues = {
    studentName: '',
    password: defaultStudentPassword,
    email: '',
  };

  const multipleMax = Math.min(5, studentNumberLimit - totalNumberOfStudents);
  const arrayToReduce = Array.from(Array(multipleMax).keys());
  const addMultipleInitialValues: FormValues = arrayToReduce.reduce(
    (acc, _, currIndex) => ({
      ...acc,
      [`studentName${currIndex}`]: '',
      [`password${currIndex}`]: defaultStudentPassword,
      [`email${currIndex}`]: '',
    }),
    {},
  );
  const initialValues: FormValues = {
    ...addSingleInitialValues,
    ...addMultipleInitialValues,
  };

  const schemaValues = Object.keys(initialValues).reduce((acc, curr, currIndex) => {
    if (curr.includes('student')) {
      return {
        ...acc,
        [curr]: Yup.string().min(4, 'Please enter a student name (min 4 characters)'),
      };
    }
    if (curr.includes('pass')) {
      return {
        ...acc,
        [curr]: Yup.string().test('has name been entered', function (value: string | undefined) {
          const studentNameKey = Object.keys(initialValues)[currIndex - 1];
          const studentNameValue = this.parent[studentNameKey];
          if (studentNameValue && studentNameValue !== '') {
            if (!value || value === '') {
              return this.createError({
                message: 'Password is required',
              });
            }
            if (value.length < 6) {
              return this.createError({
                message: 'Minimum 6 characters',
              });
            }
          }
          return true;
        }),
      };
    }
    if (curr.includes('email')) {
      return {
        ...acc,
        [curr]: Yup.string().email('Please enter a valid email address'),
      };
    }
    return acc;
  }, {});

  const validationSchema = Yup.object().shape(schemaValues);

  const handleAddStudent = async (
    values: FormValues,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
  ) => {
    try {
      const newPasswordList = [...studentPasswords, values.password];
      const newStudentNames = [...studentNames, values.studentName];
      const newEmailList = [...studentEmails, values.email || ''];
      setStudentNames(newStudentNames);
      setStudentPasswords(newPasswordList);
      setStudentEmails(newEmailList);
    } finally {
      setFieldValue('studentName', '', false);
      setFieldValue('password', values.password || defaultStudentPassword, false);
      setFieldValue('email', '', false);
    }
  };

  const handleRemoveStudent = (index: number) => {
    const newStudentNames = [...studentNames];
    const newPasswords = [...studentPasswords];
    const newEmails = [...studentEmails];
    newStudentNames.splice(index, 1);
    newPasswords.splice(index, 1);
    newEmails.splice(index, 1);
    setStudentNames(newStudentNames);
    setStudentPasswords(newPasswords);
    setStudentEmails(newEmails);
  };

  const handleSwitch = () => {
    setIsAddMultiple((prevState) => !prevState);
  };

  const handleSubmit = async (values: FormValues, { setSubmitting, setFieldError }: FormikHelpers<FormValues>) => {
    const { studentName, password, email, ...multiValues } = values;
    const multiValuesArray = Object.entries(multiValues);
    const multiNames: string[] = [];
    const multiPasswords: string[] = [];
    const multiEmails: string[] = [];
    const emailCheckTargets: EmailCheckTarget[] = [];

    const normalizedSingleEmail = (email || '').trim();

    if (normalizedSingleEmail !== '') {
      emailCheckTargets.push({ fieldName: 'email', email: normalizedSingleEmail });
    }

    multiValuesArray.reduce((acc, [k, v], currIndex) => {
      if (k.includes('student') && v !== '') {
        multiNames.push(v.trim());
        multiPasswords.push(multiValuesArray[currIndex + 1][1].trim());
        const emailFieldName = multiValuesArray[currIndex + 2][0];
        const trimmedEmail = multiValuesArray[currIndex + 2][1].trim();
        multiEmails.push(trimmedEmail);
        if (trimmedEmail !== '') {
          emailCheckTargets.push({ fieldName: emailFieldName, email: trimmedEmail });
        }
      }
      return acc;
    }, []);
    if (studentName === '' && studentNames.length === 0 && multiNames.length === 0) return null;
    setSubmitting(true);

    const queuedEmailTargets: EmailCheckTarget[] = studentEmails
      .map((queuedEmail) => queuedEmail.trim())
      .filter((queuedEmail) => queuedEmail !== '')
      .map((queuedEmail) => ({
        fieldName: 'email',
        email: queuedEmail,
      }));

    const allEmailTargets = [...queuedEmailTargets, ...emailCheckTargets];

    // One check per distinct address, not per row. Two rows carrying the same
    // email resolve to a single account, so checking twice costs an extra
    // round trip and still reports the address as free both times.
    const fieldNamesByEmail = new Map<string, string[]>();
    allEmailTargets.forEach(({ fieldName, email: candidateEmail }) => {
      const key = candidateEmail.trim().toLowerCase();
      const existing = fieldNamesByEmail.get(key);
      if (existing) {
        existing.push(fieldName);
      } else {
        fieldNamesByEmail.set(key, [fieldName]);
      }
    });

    const emailCheckResults = await Promise.all(
      [...fieldNamesByEmail.entries()].map(async ([candidateEmail, fieldNames]) => {
        try {
          const verdict = await checkStudentEmailVerdict(candidateEmail);
          return {
            fieldNames,
            // null means the email may be used, which now includes reusing an
            // intact student account the teacher already manages.
            verdictError: getStudentEmailVerdictError(verdict, 'add'),
          };
        } catch {
          return {
            fieldNames,
            verdictError: EMAIL_CHECK_FAILED_ERROR,
          };
        }
      }),
    );

    let shouldBlockSubmit = false;

    emailCheckResults.forEach((result) => {
      const error = getBatchEmailError(result.verdictError, result.fieldNames.length);

      if (error) {
        // Mark every row carrying this address, not just the first.
        result.fieldNames.forEach((fieldName) => setFieldError(fieldName, error));
        shouldBlockSubmit = true;
      }
    });

    if (shouldBlockSubmit) {
      setSubmitting(false);
      return;
    }

    try {
      let namesToAdd = [];
      let passwordsToAdd = [];
      let emailsToAdd = [];

      if (studentNames.length > 0) {
        namesToAdd = [...studentNames, ...multiNames];
        passwordsToAdd = [...studentPasswords, ...multiPasswords];
        emailsToAdd = [...studentEmails, ...multiEmails];
      } else if (studentName === '') {
        namesToAdd = multiNames;
        passwordsToAdd = multiPasswords;
        emailsToAdd = multiEmails;
      } else {
        namesToAdd = [studentName, ...multiNames];
        passwordsToAdd = [password, ...multiPasswords];
        emailsToAdd = [email || '', ...multiEmails];
      }
      onAddStudents(namesToAdd, passwordsToAdd, emailsToAdd);
      setStudentNames([]);
      setStudentPasswords([]);
      setStudentEmails([]);
      setIsAddMultiple(false);
      hideModal();
    } catch {
      console.error('Could not add student to class');
    } finally {
      setSubmitting(false);
    }
  };

  const addSingleIcon = <Icon icon={avatarSolid} />;
  const addMultipleIcon = <Icon icon={usersSolid} />;

  return (
    <div className={styles.container}>
      <div className={styles.title}>
        <h2>Add Students</h2>
        <button onClick={hideModal}>x</button>
      </div>
      <div className={styles.content}>
        <div className={styles.selectorRow}>
          <ModeSelector
            isSecondOptionChecked={isAddMultiple}
            tooltip1='Add one student at a time'
            tooltip2='Add multiple students at a single time'
            leftContent={addSingleIcon}
            rightContent={addMultipleIcon}
            primaryColor={'#ffffff'}
            secondaryColor={'#00afd8'}
            borderColor={'#012a73'}
            handleSwitch={handleSwitch}
          />
        </div>
        <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
          {({ values, setFieldError, setFieldValue, setFieldTouched }) => (
            <Form className={styles.formContainer}>
              {!isAddMultiple && (
                <AddSingleStudent
                  values={values}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                  validationAttemptCount={validationAttemptCount}
                  onValidationAttempt={() => setValidationAttemptCount((prev) => prev + 1)}
                  studentNames={studentNames}
                  handleRemoveStudent={handleRemoveStudent}
                  handleAddStudent={handleAddStudent}
                  setFieldError={setFieldError}
                />
              )}
              {isAddMultiple && <AddMultipleStudents values={values} />}
              <div className={styles.buttonRow}>
                {isAddMultiple && (
                  <p className={styles.noteForAddingMultipleStudent}>
                    Please note student passwords cannot be changed once added to your class.
                  </p>
                )}
                <Button type='submit' small darkBlue>
                  CONFIRM + ADD TO CLASS
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default AddStudents;
